import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Check, Calendar, Users, Tag, Sparkles, Quote } from "lucide-react";
import { toast } from "sonner";
import { PrivacyDialog } from "./PrivacyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { first_name: "", last_name: "", phone: "", email: "", city: "" };

// Titolo con le ultime due parole in fucsia (stile editoriale del brand).
const accentTitle = (title) => {
  const words = (title || "").split(" ").filter(Boolean);
  if (words.length <= 2) return { head: "", accent: title };
  return { head: words.slice(0, -2).join(" "), accent: words.slice(-2).join(" ") };
};

export const MasterclassBanner = () => {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [privacy, setPrivacy] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [signedUp, setSignedUp] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/masterclass/info`)
      .then((res) => setInfo(res.data))
      .catch(() => setInfo(null));
  }, []);

  // Notifica l'header quando il banner appare/scompare (adatta i colori del menu).
  useEffect(() => {
    window.dispatchEvent(new Event("mc-banner-state"));
  }, [info]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const soldOut = info?.sold_out;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.phone || !form.email) {
      toast.error("Compila nome, cognome, telefono ed email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare i termini e la privacy per continuare.");
      return;
    }
    setLoading(true);
    try {
      if (soldOut) {
        await axios.post(`${API}/masterclass/waitlist`, { ...form, privacy, company });
        setWaitlisted(true);
        toast.success("Sei in lista d'attesa! Ti avviserò per la prossima edizione.");
      } else {
        await axios.post(`${API}/masterclass/register`, { ...form, privacy, company });
        setSignedUp(true);
      }
    } catch (err) {
      if (err.response?.status === 409 && !soldOut) {
        // Posti esauriti tra caricamento pagina e invio → fallback lista d'attesa.
        setInfo((i) => (i ? { ...i, sold_out: true, spots_left: 0 } : i));
        try {
          await axios.post(`${API}/masterclass/waitlist`, { ...form, privacy, company });
          setWaitlisted(true);
          toast.success("Posti appena esauriti: ti ho messa in lista d'attesa.");
        } catch (_) {
          toast.error("Posti esauriti. Riprova tra poco.");
        }
      } else {
        toast.error("Ops, qualcosa è andato storto. Riprova tra poco.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Banner nascosto finché l'interruttore in Admin è spento (struttura e dati restano intatti).
  if (!info || !info.banner_visible) return null;

  const titleParts = accentTitle(info.title || "L'ARTE DI AVERE STILE");

  return (
    <section
      id="masterclass-banner"
      data-testid="masterclass-banner"
      className="relative overflow-hidden bg-gradient-to-br from-espresso via-[#1c1c1c] to-black text-crema"
    >
      {/* sfondo: copertina guida con velo scuro per la leggibilità */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/brand/guida-metodo-essenziale.jpg)`,
          backgroundPosition: "center center",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/75 to-black/50"
      />
      {/* soft fuchsia glow for depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-fuchsiabrand/20 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full bg-magentadark/15 blur-[130px]"
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16 sm:pb-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <span
            data-testid="mc-badge"
            className="inline-flex items-center gap-2 bg-fuchsiabrand text-white font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {info.badge_label || "Guida Digitale"}
          </span>

          <h1 className="mt-6 font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            {titleParts.head} <span className="text-fuchsiabrand">{titleParts.accent}</span>
          </h1>

          <p className="mt-6 text-base md:text-lg text-crema/75 max-w-xl leading-relaxed">
            {info.subtitle}
          </p>

          {info.bullets?.length > 0 && (
            <ul className="mt-5 space-y-2.5 max-w-xl" data-testid="mc-bullets">
              {info.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-crema/75 leading-relaxed">
                  <span className="mt-[7px] w-1.5 h-1.5 flex-shrink-0 bg-fuchsiabrand" />
                  {b}
                </li>
              ))}
            </ul>
          )}

          {info.surprise && (
            <p
              className="mt-6 max-w-xl inline-flex items-center gap-2 text-fuchsiabrand font-semibold text-base tracking-wide"
              data-testid="mc-surprise"
            >
              <Sparkles size={17} className="flex-shrink-0" />
              {info.surprise}
            </p>
          )}

          {info.manifesto && (
            <div
              className="mt-7 max-w-xl bg-fuchsiabrand/10 border border-fuchsiabrand/40 rounded-sm px-5 py-4"
              data-testid="mc-manifesto"
            >
              <Quote size={16} className="text-fuchsiabrand mb-2" />
              <p className="text-sm sm:text-base font-semibold tracking-wide text-crema leading-relaxed whitespace-pre-line">
                {info.manifesto}
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-col items-start gap-5">
            {info.event_datetime && (
              <div className="flex items-center gap-3" data-testid="mc-datetime">
                <Calendar size={18} className="text-fuchsiabrand flex-shrink-0" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-crema/40">
                    Data e ora
                  </p>
                  <p className="text-sm text-crema/90">
                    {info.event_datetime}
                    {!info.event_datetime.toLowerCase().includes("zoom") ? " su Zoom" : ""}
                  </p>
                </div>
              </div>
            )}
            {info.price && (
              <div className="flex items-center gap-3" data-testid="mc-price">
                <Tag size={18} className="text-fuchsiabrand flex-shrink-0" />
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-crema/40">
                    Prezzo bloccato
                  </p>
                  <p className="text-sm text-crema/90">{info.price}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3" data-testid="mc-spots">
              <Users size={18} className="text-fuchsiabrand flex-shrink-0" />
              <p className="text-sm font-medium text-crema/90">
                {soldOut ? "Posti esauriti — lista d'attesa aperta" : "Disponibilità limitata"}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right: form / conferma iscrizione / lista d'attesa */}
        {signedUp ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white/[0.06] backdrop-blur-md border border-crema/15 rounded-sm p-8 sm:p-12 text-center"
            data-testid="mc-signup-success"
          >
            <div className="w-14 h-14 rounded-full bg-fuchsiabrand flex items-center justify-center mx-auto mb-6">
              <Check className="text-white" size={26} />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-crema mb-3">Richiesta ricevuta!</h3>
            <p className="text-crema/70 leading-relaxed max-w-sm mx-auto">
              Abbiamo preso in carico la tua richiesta, ti ricontatteremo a breve!
            </p>
          </motion.div>
        ) : waitlisted ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white/[0.06] backdrop-blur-md border border-crema/15 rounded-sm p-8 sm:p-12 text-center"
            data-testid="masterclass-waitlisted"
          >
            <div className="w-14 h-14 rounded-full bg-fuchsiabrand flex items-center justify-center mx-auto mb-6">
              <Check className="text-white" size={26} />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-crema mb-3">Sei in lista d'attesa!</h3>
            <p className="text-crema/70 leading-relaxed max-w-sm mx-auto">
              I posti di questa edizione sono esauriti, ma ti ho aggiunta alla lista d'attesa.
              Ti avviserò via email non appena si libera un posto o apro una nuova data.
            </p>
          </motion.div>
        ) : (
        <motion.form
          onSubmit={submit}
          noValidate
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white/[0.06] backdrop-blur-md border border-crema/15 rounded-sm p-6 sm:p-9"
          data-testid="masterclass-form"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-fuchsiabrand mb-6">
            {soldOut ? "Lista d'attesa" : "Compila il modulo"}
          </p>

          <div className="space-y-6 mb-6">
            <input
              className="mc-input"
              placeholder="Nome *"
              value={form.first_name}
              onChange={update("first_name")}
              data-testid="mc-first-name"
            />
            <input
              className="mc-input"
              placeholder="Cognome *"
              value={form.last_name}
              onChange={update("last_name")}
              data-testid="mc-last-name"
            />
            <input
              className="mc-input"
              type="tel"
              placeholder="Numero di telefono *"
              value={form.phone}
              onChange={update("phone")}
              data-testid="mc-phone"
            />
            <input
              className="mc-input"
              type="email"
              placeholder="Email *"
              value={form.email}
              onChange={update("email")}
              data-testid="mc-email"
            />
            <input
              className="mc-input"
              placeholder="Città / Zona di residenza"
              value={form.city}
              onChange={update("city")}
              data-testid="mc-city"
            />
          </div>

          {/* honeypot */}
          <input
            type="text"
            name="company"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />

          <div className="flex items-start gap-3 mb-7 group" data-testid="mc-privacy-label">
            <span
              onClick={() => setPrivacy((v) => !v)}
              className={`mt-0.5 w-5 h-5 flex-shrink-0 border flex items-center justify-center cursor-pointer transition-colors duration-300 ${
                privacy ? "bg-fuchsiabrand border-fuchsiabrand" : "border-crema/30 group-hover:border-fuchsiabrand"
              }`}
              data-testid="mc-privacy-checkbox"
              role="checkbox"
              aria-checked={privacy}
            >
              {privacy && <Check size={14} className="text-white" />}
            </span>
            <span className="text-xs text-crema/70 leading-relaxed">
              Accetto i termini e la{" "}
              <PrivacyDialog fields="nome, cognome, numero di telefono, email e città/zona di residenza">
                <button
                  type="button"
                  data-testid="mc-privacy-link"
                  className="text-fuchsiabrand underline underline-offset-2 hover:text-crema transition-colors"
                >
                  Privacy Policy
                </button>
              </PrivacyDialog>{" "}
              e mi impegno a non divulgare il materiale riservato. *
            </span>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="mc-submit"
            className="group w-full bg-fuchsiabrand text-white py-4 px-6 rounded-full font-mono text-xs sm:text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-3 hover:bg-magentadark transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : soldOut ? (
              "Unisciti alla lista d'attesa"
            ) : (
              <>
                {info.cta_label || "Iscriviti"}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </>
            )}
          </button>
        </motion.form>
        )}
      </div>
    </section>
  );
};
